// moveTableToSchema — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'move-table-schema' })` op (DSE2 §3.D1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D1 — Table rename + schema-move UI.
//
// Per-engine support:
//   - mssql:    ALTER SCHEMA <newSchema> TRANSFER <fromTable>.
//   - postgres: ALTER TABLE <fromTable> SET SCHEMA <newSchema>.
//   - mysql:    unsupported per-row — mysql conflates schemas with databases.
//               The rename-table op with `mysqlToSchema` is the engine-correct
//               idiom (`RENAME TABLE old_db.old TO new_db.old`); the consumer
//               UI surfaces a disabled affordance with a documented redirect.
//   - oracle:   unsupported — oracle conflates schemas with users; cross-
//               schema moves require a CREATE-AS-SELECT migration that v2
//               leaves to the consumer.
//   - sqlite:   unsupported — sqlite has no DDL schemas.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface MoveTableToSchemaArgs {
  /** The existing table to move. Must carry a schema qualifier on
   *  mssql + pgsql for unambiguous emit. */
  readonly fromTable: QualifiedName;
  /** Target schema name. */
  readonly toSchema: string;
}

export function buildMoveTableToSchemaEdit(args: MoveTableToSchemaArgs): DdlEdit {
  return {
    op: 'move-table-schema',
    fromTable: args.fromTable,
    toSchema: args.toSchema,
  };
}

export async function commitMoveTableToSchema(
  ctx: VqeDdlEditContext,
  args: MoveTableToSchemaArgs,
): Promise<void> {
  const edit = buildMoveTableToSchemaEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const SUPPORTED_ENGINES: ReadonlySet<SqlEngine> = new Set([
  'mssql',
  'postgres',
] as const);

export function isMoveTableToSchemaSupported(engine: SqlEngine): boolean {
  return SUPPORTED_ENGINES.has(engine);
}

export function getMoveTableToSchemaUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL conflates schemas with databases. Use Rename table with a target '
      + 'schema set to do the cross-database equivalent '
      + '(RENAME TABLE old_db.old TO new_db.old).'
    );
  }
  if (engine === 'oracle') {
    return (
      'Oracle conflates schemas with users. Moving a table between schemas '
      + 'requires a CREATE TABLE … AS SELECT + DROP TABLE migration that the '
      + 'visual editor does not synthesize.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no DDL schemas. Use ATTACH DATABASE if a logical separation '
      + 'is needed.'
    );
  }
  return '';
}

/** Pre-flight validator. Returns `null` when args are acceptable. */
export function validateMoveTableToSchemaForEngine(
  engine: SqlEngine,
  args: MoveTableToSchemaArgs,
): string | null {
  if (!isMoveTableToSchemaSupported(engine)) {
    return getMoveTableToSchemaUnsupportedTooltip(engine);
  }
  const trimmed = args.toSchema.trim();
  if (trimmed.length === 0) {
    return 'Target schema is required.';
  }
  if (
    args.fromTable.schema !== undefined
    && args.fromTable.schema.length > 0
    && args.fromTable.foldedSchema !== undefined
    && args.fromTable.foldedSchema === trimmed.toLowerCase()
  ) {
    return 'Target schema is the same as the source schema.';
  }
  return null;
}
